/**
 * useLivretSync.js — Hook de synchronisation locale ↔ cloud d'un livret.
 *
 * Contrat :
 *   const { state, save, saveProfil, done, loading, syncing } =
 *     useLivretSync(storageKey);
 *
 * Comportement :
 *  - Sauvegarde locale : localStorage (toujours actif, même hors ligne / non
 *    connecté).
 *  - Sauvegarde cloud : si `user` connecté → PUT /api/livrets/progression/{key}
 *    en debounce ~800 ms sur chaque modification.
 *  - Fetch initial : à l'ouverture, si `user` connecté → GET puis merge avec
 *    localStorage (bool `true` prioritaire, profil = union non destructive)
 *    et POST /merge pour aligner le cloud.
 *  - Migration login : si l'utilisateur se connecte alors que le hook est
 *    déjà monté, un `useEffect` déclenche la migration automatique de l'état
 *    local vers le cloud.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useApp } from '../contexts/AppContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function loadLocal(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}'); }
  catch (_) { return {}; }
}
function saveLocal(key, state) {
  try { localStorage.setItem(key, JSON.stringify(state)); }
  catch (_) { /* quota exceeded — silencieux */ }
}

function isEmpty(state) {
  if (!state) return true;
  const keys = Object.keys(state).filter(k => !k.startsWith('__'));
  return keys.length === 0 && !(state.__profil && Object.values(state.__profil).some(Boolean));
}

function mergeStates(cloud, local) {
  const out = { ...(cloud || {}) };
  Object.entries(local || {}).forEach(([k, v]) => {
    if (k === '__profil') {
      const merged = { ...((cloud || {}).__profil || {}) };
      if (v && typeof v === 'object') {
        Object.entries(v).forEach(([pk, pv]) => { if (pv && !merged[pk]) merged[pk] = pv; });
      }
      out.__profil = merged;
    } else if (v === true) {
      out[k] = true;
    } else if (!(k in out)) {
      out[k] = v;
    }
  });
  return out;
}

export default function useLivretSync(storageKey) {
  const { user, token } = useApp();
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const debounceRef = useRef(null);
  const initialFetchDoneRef = useRef(false);
  const previousUserRef = useRef(null);

  const auth = useMemo(() => (token ? { headers: { Authorization: `Bearer ${token}` } } : null), [token]);

  const cloudPut = useCallback(async (nextState) => {
    if (!user || !token || !BACKEND_URL) return;
    try {
      setSyncing(true);
      await axios.put(
        `${BACKEND_URL}/api/livrets/progression/${encodeURIComponent(storageKey)}`,
        { state: nextState },
        auth,
      );
    } catch (_) { /* silencieux — reprend au prochain save */ }
    finally { setSyncing(false); }
  }, [user, token, auth, storageKey]);

  const cloudMerge = useCallback(async (localState) => {
    if (!user || !token || !BACKEND_URL) return null;
    try {
      setSyncing(true);
      const { data } = await axios.post(
        `${BACKEND_URL}/api/livrets/progression/${encodeURIComponent(storageKey)}/merge`,
        { state: localState },
        auth,
      );
      return data?.state || null;
    } catch (_) { return null; }
    finally { setSyncing(false); }
  }, [user, token, auth, storageKey]);

  // ---- Chargement initial + migration -----------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const local = loadLocal(storageKey);
      // 1) Toujours partir de l'état local (accessible offline / non connecté)
      if (!cancelled) setState(local);

      // 2) Si connecté, fusionner avec le cloud
      if (user && token && BACKEND_URL) {
        try {
          const cloud = isEmpty(local)
            ? (await axios.get(
                `${BACKEND_URL}/api/livrets/progression/${encodeURIComponent(storageKey)}`,
                auth,
              )).data?.state
            : (await cloudMerge(local));
          if (!cancelled && cloud) {
            const merged = isEmpty(local) ? (cloud || {}) : mergeStates(cloud, local);
            setState(merged);
            saveLocal(storageKey, merged);
          }
        } catch (_) { /* fallback silencieux sur l'état local */ }
      }
      if (!cancelled) {
        setLoading(false);
        initialFetchDoneRef.current = true;
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // ---- Migration automatique quand l'utilisateur se connecte -------------
  useEffect(() => {
    const previous = previousUserRef.current;
    previousUserRef.current = user;
    if (!initialFetchDoneRef.current) return;
    // Détection : passage de non-connecté → connecté
    if (!previous && user && token) {
      const local = loadLocal(storageKey);
      if (!isEmpty(local)) {
        cloudMerge(local).then((cloud) => {
          if (cloud) {
            setState(cloud);
            saveLocal(storageKey, cloud);
          }
        });
      }
    }
  }, [user, token, storageKey, cloudMerge]);

  // ---- Sauvegarde partielle (debouncée pour le cloud) --------------------
  const save = useCallback((partial) => {
    setState(prev => {
      const next = { ...prev, ...partial };
      saveLocal(storageKey, next);
      // Debounce cloud
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => { cloudPut(next); }, 800);
      return next;
    });
  }, [storageKey, cloudPut]);

  const setValue = useCallback((key, value) => save({ [key]: value }), [save]);
  const saveProfil = useCallback((profil) => save({ __profil: profil }), [save]);

  const done = useMemo(() => (
    Object.keys(state).filter(k => !k.startsWith('__') && state[k] === true).length
  ), [state]);

  return { state, setValue, save, saveProfil, done, loading, syncing };
}
